import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { kosongkan } from "../lib/prefetch";
import { clearAddressCache } from "../lib/addressBookCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [ready, setReady] = useState(false);
  // PR login Google (review Codex 1): setiap sesi baru dipasang / keluar menaikkan generasi. Jawaban /me yang dimulai untuk sesi sebelumnya
  // (mis. token lama saat halaman /masuk-google menukar kode) tidak boleh menimpa pengguna baru atau menghapus token baru.
  const generasi = useRef(0);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("hs_token");
    const gen = generasi.current;
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      if (gen === generasi.current) setUser(data);
    } catch {
      if (gen === generasi.current && localStorage.getItem("hs_token") === token) {
        localStorage.removeItem("hs_token");
        setUser(false);
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // PR46 tambahan 29/10/2026: simpanan memori (lib/prefetch, mis. produk Katalog) dibuang saat keluar / ganti akun / masuk
  const akunTerakhir = useRef(undefined);
  const kunciAkun = user === null ? null : user ? String(user.id ?? user.email ?? "akun") : "tamu";
  useEffect(() => {
    if (kunciAkun === null) return;
    // Buku alamat TIDAK dikosongkan di sini (review Codex B2 putaran 2): efek ini berjalan SESUDAH efek halaman (mis. Checkout) yang sudah
    // meminta alamat akun baru — mengosongkan di sini membuang jawaban sah akun baru. Buku alamat dikosongkan sinkron di pasangSesi/keluar,
    // dan cache-nya terikat token sesi (lib/addressBookCache).
    if (akunTerakhir.current !== undefined && akunTerakhir.current !== kunciAkun) kosongkan();
    akunTerakhir.current = kunciAkun;
  }, [kunciAkun]);

  // PR keamanan lanjutan B (audit lead 13): buku alamat akun sebelumnya dibuang SEBELUM token & pengguna baru dipasang (sinkron)
  const pasangSesi = (token, u) => {
    generasi.current += 1;
    clearAddressCache();
    localStorage.setItem("hs_token", token);
    setUser(u);
    setReady(true);   // review Codex 2: sesi baru sudah pasti; jangan menunggu /me basi (RequireAuth tidak tertahan di layar memuat)
    return u;
  };

  // PR8: token admin hanya 24 jam dan token bisa dicabut (ganti sandi). Bila server menolak token yang dikirim, sesi dibersihkan
  // supaya halaman yang butuh akun langsung beralih ke Masuk (bukan menampilkan galat berulang).
  useEffect(() => {
    const TOKEN_DITOLAK = ["Token kedaluwarsa", "Token tidak valid", "Tipe token tidak valid", "Pengguna tidak ditemukan"];
    const id = api.interceptors.response.use((r) => r, (err) => {
      // review Codex 1: hanya bila token yang ditolak = token yang sekarang tersimpan (jawaban basi untuk sesi lama tidak mengeluarkan sesi baru)
      const dipakai = String(err.config?.headers?.Authorization || "").replace(/^Bearer /, "");
      if (err.response?.status === 401 && dipakai && dipakai === localStorage.getItem("hs_token") && TOKEN_DITOLAK.includes(err.response?.data?.detail)) {
        localStorage.removeItem("hs_token");
        clearAddressCache();
        setUser(false);
      }
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    return pasangSesi(data.access_token, data.user);
  };

  // Dipakai setelah atur ulang kata sandi: sesi baru seperti login biasa.
  const applySession = (data) => pasangSesi(data.access_token, data.user);

  const register = async (payload) => {    const { data } = await api.post("/api/auth/register", payload);
    return pasangSesi(data.access_token, data.user);
  };

  const logout = () => {
    generasi.current += 1;
    localStorage.removeItem("hs_token");
    clearAddressCache();
    kosongkan();
    setUser(false);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/api/auth/profile", payload);
    setUser(data);
    return data;
  };

  const updateEmail = async (email, current_password) => {
    const { data } = await api.put("/api/auth/email", { email, current_password });
    localStorage.setItem("hs_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const changePassword = async (payload) => {
    const { data } = await api.put("/api/auth/password", payload);
    // PR8: ganti sandi menaikkan versi token (sesi lain keluar); sesi ini memakai token baru dari jawaban
    if (data && data.access_token) localStorage.setItem("hs_token", data.access_token);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, updateProfile, updateEmail, changePassword, applySession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
